import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * DraftBar
 *
 * Two-row omnibar layout: an auto-growing textarea on top, a
 * `--omnibar-meta-h` meta row beneath it carrying the destination
 * chip. No collapsed posture — the bar always reads at its full width
 * with the meta row visible, so users can see where a capture lands
 * without having to engage the input first. The coral send button is
 * anchored to the meta row's right gutter so it stays at a fixed
 * screen position whether the pill is empty or holding a multi-line
 * draft.
 *
 *   - Top row: the input surface. Single-line height matches
 *     `--omnibar-h` (48px); each additional line grows the row by 24px
 *     (line-height) up to a 6-line cap (~160px), after which the
 *     surrounding `<ScrollView>` wrapper takes over with overlay
 *     scrollbars. Both modes use a `<ScrollView>` for overflow:
 *     Ask wraps a `<textarea>`, Add wraps the Tiptap contenteditable
 *     (see `@/components/rich-text/rich-text-editor.tsx`).
 *   - Meta row (bottom): destination chip on the left, always visible.
 *   - Send button: absolutely positioned at `bottom: 10px; right: 8px`
 *     relative to the pill — always sits in the meta row's right-side
 *     gutter.
 *
 * Built from primitives in `@shared/client/components/ui/*`:
 *
 *   - `<MultiSelectPopover>` — destination picker. Lets the user pick N
 *     collections to drop the new block into. Empty selection means
 *     "Stream" (no destination beyond the user's primary capture).
 *   - `<Button>` — circular coral send action, tone overridden to coral
 *     via `--button-tone-rgb` in the host SCSS.
 *
 * Submitting differs by mode (the send button itself works in either
 * mode):
 *
 *   - Add: `⌘/Ctrl+Enter` commits, plain Enter inserts a newline so the
 *     textarea can carry multi-line drafts without an accidental commit
 *     on Return. Submit calls `blockStore.add({ content, collectionIds
 *     })`, which inserts an optimistic placeholder (in `pending` state)
 *     and kicks off server ingest. The first selected collection rides
 *     along on the ingest payload; any additional collections are
 *     filled in via `collectionStore.copyBlocks` once the real envelope
 *     arrives.
 *   - Ask: plain Enter commits (questions are usually single-line and
 *     this matches chat-app convention); `Shift+Enter` still inserts a
 *     newline. Submit fires `sendAssistantQuery` and opens the
 *     transient response collection in a new tab.
 *
 * Focus on the textarea dims the stream content via
 * `app.setStreamFadeOpacity` and triggers the `:focus-within` reveal of
 * the meta row (handled in `omnibar-host.scss`).
 */
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FaIcon } from "@shared/client/components/fa-icon";
import { MultiSelectPopover } from "@shared/client/components/ui/multi-select-popover/multi-select-popover";
import { Button } from "@shared/client/components/ui/button/button";
import { KeyboardShortcut } from "@shared/client/components/ui/keyboard-shortcut/keyboard-shortcut";
import { ScrollView } from "@shared/client/components/scroll-view/scroll-view";
import { faAdd, faArrowUp, faQuestion, faUpRightAndDownLeftFromCenter, faDownLeftAndUpRightToCenter, } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { isEmptyTiptapDoc } from "@shared/types/tiptap-document";
import { showToast } from "@shared/client/components/toast/show-toast";
import { useApp } from "@/state/app-context";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
import { WipBorder } from "@/components/wip-border/wip-border";
import { useActiveCollectionId } from "@/lib/urls";
import { urlForMemberOnlyEntity } from "@shared/types/urls";
import { focusOmnibarInput, focusOmnibarInputOnNextRegister, openOmnibarFirstResult, registerOmnibarExpand, registerOmnibarInput, registerOmnibarMinimize, } from "./omnibar-bridge";
import { extractFiles } from "@/components/rich-text/rich-text-clipboard";
// Lazy-loaded: `<RichTextEditor>` pulls Tiptap + every extension we
// use (~500 KB minified). The omnibar mounts on every screen but the
// editor itself only renders when the user activates Add mode — so
// keeping Tiptap out of the cold-load bundle is a win even for users
// who do eventually capture. The fallback is `null` because the
// surrounding `.omnibar-draft__row--input` cell already carries the
// pill background and the chunk lands in ~50-200 ms typically.
//
// The `webpackChunkName: "rich-text-editor"` magic comment names the
// chunk so the same lazy import in `block-detail-edit-shell.tsx` lands
// in the same chunk — clicking Edit on a saved text block after
// drafting in the omnibar (or vice versa) reuses the already-fetched
// Tiptap code instead of triggering a second network round-trip.
//
// The lazy boundary still forwards refs (React 19 supports refs on
// lazy components natively), so `editorRef.current?.focus()` keeps
// working — it just no-ops until the chunk lands and the editor
// mounts. Consumers already guard with optional chaining.
const RichTextEditor = React.lazy(() => import(
/* webpackChunkName: "rich-text-editor" */ "@/components/rich-text/rich-text-editor").then((m) => ({
    default: m.RichTextEditor,
})));
import { DraftToolbar } from "./draft-toolbar";
import { DraftAttachments } from "./draft-attachments";
import { DraftAttachmentChip } from "./draft-attachment-chip";
import { LiveWaveform } from "./live-waveform";
import { useVoiceRecorder } from "./use-voice-recorder";
import { disposeStagedAttachment, MAX_STAGED_ATTACHMENTS, stageFile, stageImage, stageVoice, } from "./staged-attachment";
import { sendAssistantQuery } from "@/api/query-api";
import { newTransientId, startQuery, setQueryReady, setQueryError, } from "@/data/assistant-query-store";
import { openCollectionTab } from "@/screens/app-screen/app-tabs-store";
import { ConfiguredLogger } from "@/lib/logger";
import { loadDraft, saveDraft, clearDraft } from "@/state/omnibar-draft-store";
import { track } from "@/lib/analytics";
import { queryLengthBucket } from "@/lib/entry-context";
const draftBarLogger = new ConfiguredLogger({ caller: "DraftBar" });
const STREAM_FADE_OPACITY_FOCUSED = 0.9;
const STREAM_FADE_OPACITY_IDLE = 1;
// The textarea's per-line height + top padding, used by the caret-
// into-view helper to compute the caret's y-position. Must stay in
// sync with `.omnibar-draft__input`'s `line-height` + `padding-top` in
// `omnibar-host.scss`. The 6-line visible cap itself lives on the
// `<ScrollView>` wrapper as `max-height: 160px` in `.omnibar-draft__
// textarea-scroll` (Ask mode) and `.omnibar-draft__editor-scroll`
// (Add mode); JS no longer pins the textarea at the cap because the
// ScrollView's overlay scrollbars take over once content exceeds it.
const TEXTAREA_LINE_HEIGHT = 24;
const TEXTAREA_PADDING_TOP = 12;
/**
 * How long to wait between Add-mode editor updates before persisting the
 * draft to localStorage. Tuned for "feels saved without thrashing the
 * disk on every keystroke" — fast enough that an accidental tab close
 * after a brief pause won't lose anything, slow enough that bursty
 * typing collapses into a single write.
 */
const DRAFT_SAVE_DEBOUNCE_MS = 400;
// Idle-chip label when no collections are selected. Captures into the
// user's "primary" collection (the default Stream) without an explicit
// destination.
const STREAM_LABEL = "Stream";
// Swatch colour shown on the chip in the "Stream" / multi-selection
// states, where there is no single collection colour to surface.
const NEUTRAL_SWATCH = "rgb(220, 220, 218)";
const COLLECTION_FALLBACK_SWATCH = "rgb(180, 180, 178)";
const DraftDestinationChip = memo(function DraftDestinationChip({ routeCollectionId, chipLabel, chipSwatch, setSelectedCollectionIds, }) {
    const app = useApp();
    const openStackId = app.useStore((s) => s.ui.openStackId);
    const activeCollectionId = openStackId ?? routeCollectionId;
    useEffect(() => {
        setSelectedCollectionIds(activeCollectionId ? new Set([activeCollectionId]) : new Set());
    }, [activeCollectionId, setSelectedCollectionIds]);
    const chipSwatchStyle = useMemo(() => ({ background: chipSwatch }), [chipSwatch]);
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
    }, []);
    return (_jsxs("button", { type: "button", className: "omnibar-draft__chip", "aria-label": `Destination: ${chipLabel}`, 
        // Don't let the chip steal mousedown focus from the textarea —
        // the meta row stays revealed via Radix's `data-state="open"` on
        // the trigger (handled by `:has([data-state="open"])` in SCSS).
        onMouseDown: handleMouseDown, children: [_jsx("span", { className: "omnibar-draft__chip-swatch", style: chipSwatchStyle, "aria-hidden": "true" }), _jsx("span", { className: "omnibar-draft__chip-label", children: chipLabel }), _jsx("span", { className: "omnibar-draft__chip-caret", "aria-hidden": "true", children: _jsx("svg", { width: "10", height: "10", viewBox: "0 0 10 10", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("path", { d: "M2.5 4 L5 6.5 L7.5 4" }) }) })] }));
});
export const DraftBar = memo(() => {
    const app = useApp();
    const navigate = useNavigate();
    // Two backings for the input row, one mounted at a time depending on
    // mode. `textareaRef` drives Ask mode (single-line query input,
    // textarea + auto-grow mirror). `editorRef` drives Add mode (rich-
    // text Tiptap editor, auto-grows naturally via CSS). Both implement
    // the small `OmnibarInputHandle` interface the bridge stores so
    // `⌘D`/`⌘F` and the focus-and-select-on-second-press behaviour work
    // identically against either.
    const textareaRef = useRef(null);
    const editorRef = useRef(null);
    const draftRootRef = useRef(null);
    // Ref to the `<ScrollView>` wrapping the Ask-mode textarea — used to
    // reach the OverlayScrollbars viewport so the caret-into-view helper
    // can scroll the wrapper (the textarea no longer scrolls internally;
    // see the auto-grow `useLayoutEffect` for why). Typed via React's
    // component-ref inference so we don't pull `OverlayScrollbarsComponentRef`
    // through the viewer package directly.
    const textareaScrollRef = useRef(null);
    // The bar runs in one of two modes: "add" (capture a memory into the
    // stream / a collection — the original behavior) or "ask" (send a
    // conversational query to Putty and open the response as a transient
    // smart-collection tab). The user switches modes by clicking the
    // toggle pair on the right, or with the global ⌘D (Add) / ⌘F (Ask)
    // shortcuts (see `use-omnibar-focus-shortcut.ts`).
    //
    // Source-of-truth lives on the App store so siblings of the bar (the
    // host's filter strip, the rise gradient extension) can read it
    // without prop-drilling. `setMode` here is a thin wrapper that pushes
    // through `app.setOmnibarMode` — and, when the swap is happening
    // while the user is actively drafting, queues a focus on the new
    // mode's input so the textarea ↔ editor remount doesn't drop focus
    // to `body`. The bridge's `focusOmnibarInputOnNextRegister` flag is
    // consumed by the next handle registration (driven by the `[mode]`-
    // keyed effect below), which lands focus directly on the freshly-
    // mounted surface.
    const mode = app.useStore((s) => s.ui.omnibarMode);
    const setMode = useCallback((next) => {
        const current = app.state.ui.omnibarMode;
        const value = typeof next === "function"
            ? next(current)
            : next;
        if (value === current)
            return;
        const wasFocused = draftRootRef.current?.matches(":focus-within") === true;
        app.setOmnibarMode(value);
        if (wasFocused) {
            focusOmnibarInputOnNextRegister();
        }
    }, [app]);
    const [isAsking, setIsAsking] = useState(false);
    // `isModeChanging` flips on for the duration of the wash animation
    // whenever the mode changes. The wash overlay reads this attribute via
    // CSS to play its keyframe animation; we time-out back to false so the
    // animation runs once per change.
    const [isModeChanging, setIsModeChanging] = useState(false);
    // Tracks whether the user has interacted with the mode at least once.
    // Skips the wash on the very first render so the bar doesn't animate
    // a mode it was already mounted in.
    const hasModeChanged = useRef(false);
    useEffect(() => {
        if (!hasModeChanged.current) {
            hasModeChanged.current = true;
            return;
        }
        setIsModeChanging(true);
        const t = window.setTimeout(() => setIsModeChanging(false), 540);
        return () => window.clearTimeout(t);
    }, [mode]);
    // Hidden read-only textarea used purely to measure the natural content
    // height. The standard auto-grow trick (reset live textarea's height
    // to `auto`, read `scrollHeight`, write the new px height) defeats
    // CSS transitions on `height` because the `auto` reset is committed
    // mid-effect and the transition's start/end values collapse to the
    // same value. Measuring a separate mirror sidesteps the issue: the
    // live textarea's height only ever moves between transition-target
    // pixel values.
    //
    // Using a `<textarea>` (rather than a `<div>`) for the mirror means
    // line-counting semantics match the live textarea exactly — no
    // sentinel-character heuristics for trailing newlines or empty
    // content. The mirror is pinned at `height: 0` in CSS so its own
    // `scrollHeight` always reflects content height, regardless of past
    // values.
    const mirrorRef = useRef(null);
    const { collections } = collectionStore.useList();
    // Active collection from the current route — resolved across every
    // collection-rendering URL shape (`/g/{id}`, `/c/{slug}`,
    // `/{name}/{collectionSlug}`, legacy `/collection/{id}`). Undefined
    // means we're not browsing inside a particular collection (home,
    // profile, block deeplink, etc.) — the destination defaults to
    // "Stream".
    const routeCollectionId = useActiveCollectionId();
    // Items shown in the destination popover. Drops the user's "primary"
    // collection (their personal stream) since that's already the
    // implicit default — picking nothing means "Stream", and picking
    // anything additionally drops the block into those collections.
    const popoverItems = useMemo(() => collections
        .filter((c) => c.type !== "primary")
        .map((c) => ({
        id: c.id,
        label: c.title || "Untitled",
        badge: c.type === "shared" ? "shared" : undefined,
    })), [collections]);
    const [selectedCollectionIds, setSelectedCollectionIds] = useState(
    // Seed from the route on first mount so a refresh inside a
    // collection page already shows that collection ticked.
    () => (routeCollectionId ? new Set([routeCollectionId]) : new Set()));
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    // Quick-filter text bound to the destination popover's inline filter
    // input. Substring-matched case-insensitively against each item's
    // label; users with a long collections list (40+) get an
    // address-bar-style narrow-by-typing experience instead of having
    // to scroll through the full set every time. Cleared whenever the
    // popover closes (see `handlePickerOpenChange` below) so the next
    // open starts with a fresh filter.
    const [collectionFilter, setCollectionFilter] = useState("");
    const filteredPopoverItems = useMemo(() => {
        const query = collectionFilter.trim().toLowerCase();
        if (query.length === 0)
            return popoverItems;
        return popoverItems.filter((item) => item.label.toLowerCase().includes(query));
    }, [popoverItems, collectionFilter]);
    // Bridge `onOpenChange` so we can reset the filter on close. Wrapped
    // in a useCallback so the popover doesn't re-render on every parent
    // render with a fresh handler identity.
    const handlePickerOpenChange = useCallback((nextOpen) => {
        setIsPickerOpen(nextOpen);
        if (nextOpen === false) {
            // Clear on close, not on open — the user might briefly close + reopen
            // (Escape misfire, focus shuffle) and a fresh open should feel like
            // a fresh start rather than carrying over a stale filter.
            setCollectionFilter("");
        }
    }, []);
    // Tracks whether focus currently lives inside the omnibar
    // (editor in Add mode, textarea in Ask mode). Drives the
    // `data-active` data attribute on the draft root below, which the
    // viewer toaster's lift rule (`.viewer-toaster` in `app.scss`)
    // reads via root-anchored `:has(.omnibar-draft[data-active])` to
    // slide queued toasts up so they stay clear of the omnibar's
    // active footprint. Attribute trigger rather than `:focus-within`
    // because root-anchored `:has()` doesn't reliably re-evaluate on
    // descendant pseudo-class changes across browsers — same
    // rationale called out in the toaster lift rule's comment.
    const [isFocused, setIsFocused] = useState(false);
    // Single-selection chip must resolve from the full collection cache
    // (`useFindById`), not `useList()` — Group-rooted Collections are
    // excluded from the latter but still appear in the URL + seeded
    // `selectedCollectionIds`.
    const singleSelectedCollectionId = selectedCollectionIds.size === 1
        ? selectedCollectionIds.values().next().value
        : undefined;
    const resolvedSingleSelectedCollection = collectionStore.useFindById(singleSelectedCollectionId);
    // Chip label + swatch track the current selection. Empty → "Stream"
    // (the default). Single → the collection's title and colour. Multi
    // → "N collections" with a neutral swatch.
    const { chipLabel, chipSwatch } = useMemo(() => {
        if (selectedCollectionIds.size === 0) {
            return { chipLabel: STREAM_LABEL, chipSwatch: NEUTRAL_SWATCH };
        }
        if (selectedCollectionIds.size === 1 && singleSelectedCollectionId !== undefined) {
            const match = resolvedSingleSelectedCollection?.id === singleSelectedCollectionId
                ? resolvedSingleSelectedCollection
                : undefined;
            return {
                chipLabel: match?.title || "Untitled",
                chipSwatch: match?.metadata.color || COLLECTION_FALLBACK_SWATCH,
            };
        }
        return {
            chipLabel: `${selectedCollectionIds.size} collections`,
            chipSwatch: NEUTRAL_SWATCH,
        };
    }, [resolvedSingleSelectedCollection, selectedCollectionIds, singleSelectedCollectionId]);
    // `text` is the current plaintext value, shared across modes (matches
    // the bar's pre-rich-text behaviour where one text state powered both
    // surfaces). In Ask mode it's the textarea's controlled value; in Add
    // mode it's `editorRef.current.getText()` mirrored on every editor
    // change. Both paths feed the home-stream filter via
    // `app.setOmnibarQuery` and the `canSend` gate identically.
    //
    // `doc` is the rich Tiptap document, only meaningful in Add mode.
    // The editor maintains its own internal source-of-truth; this is the
    // React-state mirror that powers (a) draft persistence to localStorage,
    // (b) `blockStore.add({ doc })` on submit, (c) seeding the editor's
    // initialDoc on mount-time draft restoration. Initial value comes from
    // localStorage so a refresh restores in-flight composition.
    const initialDocFromStorage = useMemo(() => loadDraft("add"), []);
    const [text, setText] = useState("");
    const [doc, setDoc] = useState(initialDocFromStorage);
    // ── Staged attachments ────────────────────────────────────────────────
    //
    // Attachments don't upload on the spot any more — the user picks them,
    // they appear as chips above the input row, and the actual
    // `blockStore.add` call happens at send-time alongside any caption
    // text. The chip row is the user's preview-and-edit window between
    // "I picked this file" and "I committed it to my stream".
    //
    // Each staged record owns an id (for the chip's React key + remove
    // handle), a `File` (forwarded to `blockStore.add` on send), and a
    // type-specific preview payload (image object URL, voice peaks +
    // duration). The `disposeStagedAttachment` helper revokes any
    // browser-side resources (today: image object URLs) so a rapid
    // attach/remove cycle doesn't leak blob references.
    //
    // Declared here (before `canSend` and the meta-row JSX, both of
    // which read the array) so the variable is in scope for every
    // downstream consumer. The handler callbacks + recorder hook
    // declared further down depend on `setStagedAttachments`, which is
    // available immediately as a stable identity from `useState`.
    const [stagedAttachments, setStagedAttachments] = useState([]);
    // ── Voice recording ───────────────────────────────────────────────────
    //
    // The recorder hook owns the MediaRecorder + AnalyserNode lifecycle
    // and surfaces three pieces of reactive state we use across the
    // bar: `isRecording` (drives the mic button glyph + the live
    // waveform overlay), `durationSeconds` + `liveLevels` (drive the
    // overlay's contents). Completed recordings come back via the
    // `onComplete` callback below and stage as a voice chip.
    //
    // Hoisted alongside the staged-attachments state so `canSend` (and
    // its dependents — the minimize handler, send button) can read
    // `recorder.isRecording` without forward references.
    const recorder = useVoiceRecorder({
        onComplete: useCallback((file, peaks, durationSeconds) => {
            const staged = stageVoice(file, peaks, durationSeconds);
            setStagedAttachments((curr) => {
                if (curr.length >= MAX_STAGED_ATTACHMENTS) {
                    disposeStagedAttachment(staged);
                    showToast.warning(`You can attach up to ${MAX_STAGED_ATTACHMENTS} items at a time`, { id: "omnibar-attachment-limit" });
                    return curr;
                }
                return [...curr, staged];
            });
        }, []),
    });
    // Mirror the staged list into a ref so the minimize handler + the
    // unmount-only cleanup effect can read the latest contents without
    // forcing those effects to depend on the array (which would tear
    // down + recreate them on every stage / remove). Mid-session disposes
    // happen inside `removeStagedAttachment` + the send path; the
    // ref-backed reads are the safety net for "user closed the tab
    // mid-draft" and "filter strip activated a result mid-draft".
    const stagedAttachmentsRef = useRef(stagedAttachments);
    stagedAttachmentsRef.current = stagedAttachments;
    // Single entry point for adding files to the staged list. Used by:
    //
    //   • The attachment-button row (image / camera / file picker)
    //   • The document-level paste handler (image from clipboard)
    //   • The editor's `onFilePaste` (any file pasted inside the
    //     contenteditable)
    //
    // Branches on MIME (`image/*` → image preview, else → generic file
    // chip) so the staging path is the same regardless of which source
    // produced the file — a `.png` picked through the file button gets
    // the thumbnail treatment, a `.pdf` pasted from the clipboard gets
    // the filename treatment, neither needs the call site to know which
    // chip variant to construct.
    //
    // Defence-in-depth cap: `MAX_STAGED_ATTACHMENTS` is enforced inside
    // the functional updater so concurrent stage calls (rapid clicks)
    // race correctly against the live list length, not a captured
    // closure snapshot.
    const stageFiles = useCallback((incoming) => {
        if (incoming.length === 0)
            return;
        // Build all the staged records up front (each image creates an
        // object URL + kicks off async dimension probing). If the cap
        // chops some of them, we dispose the overflow inside the setter.
        const built = incoming.map((file) => {
            if (file.type.startsWith("image/")) {
                // Hold the id outside the stageImage call so the dimensions
                // callback below can find the right record. stageImage
                // returns a fresh id each call — we use that id in the
                // closure that runs once the image decodes.
                const staged = stageImage(file, (dimensions) => {
                    setStagedAttachments((curr) => curr.map((a) => a.id === staged.id && a.type === "image"
                        ? { ...a, imageDimensions: dimensions }
                        : a));
                });
                return staged;
            }
            return stageFile(file);
        });
        setStagedAttachments((curr) => {
            const remaining = MAX_STAGED_ATTACHMENTS - curr.length;
            if (remaining <= 0) {
                // Already at capacity — dispose every newly-built record and
                // warn the user. A `warn` (not `error`) because the existing
                // attachments are still valid; this is a "hold up, slow down"
                // rather than "something broke".
                for (const att of built)
                    disposeStagedAttachment(att);
                showToast.warning(`You can attach up to ${MAX_STAGED_ATTACHMENTS} items at a time`, { id: "omnibar-attachment-limit" });
                return curr;
            }
            const accepted = built.slice(0, remaining);
            const overflow = built.slice(remaining);
            for (const att of overflow)
                disposeStagedAttachment(att);
            if (overflow.length > 0) {
                showToast.warning(`Only the first ${remaining} attached — limit is ${MAX_STAGED_ATTACHMENTS}`, { id: "omnibar-attachment-limit" });
            }
            return [...curr, ...accepted];
        });
    }, []);
    // Bridge from the attachment-row's `(files, source)` callback to the
    // shared staging path. `source` is mirrored into analytics-relevant
    // metadata at send-time; the stage itself doesn't care which button
    // produced the file (MIME-sniffing already covers that).
    const handleAttachedFiles = useCallback((files, _source) => {
        stageFiles(files);
    }, [stageFiles]);
    // Bridge for the paste handlers (document-level + the editor's
    // onFilePaste). Stages pasted files via the same path attachments
    // take, so the chip row presents pasted screenshots / PDFs /
    // archives alongside button-attached files for a unified preview-
    // and-send experience.
    const uploadPastedFiles = useCallback((files) => stageFiles(files), [stageFiles]);
    // Remove a staged attachment by id. Disposes any owned resources
    // (image object URLs) so a rapid attach/remove cycle doesn't leak
    // blob references back into the heap.
    const removeStagedAttachment = useCallback((id) => {
        setStagedAttachments((curr) => {
            const target = curr.find((a) => a.id === id);
            if (target !== undefined)
                disposeStagedAttachment(target);
            return curr.filter((a) => a.id !== id);
        });
    }, []);
    const handleToggleRecording = useCallback(() => {
        if (recorder.isRecording) {
            recorder.stop();
            return;
        }
        void recorder.start().catch((err) => {
            const msg = err instanceof Error ? err.message : "Unknown error";
            draftBarLogger.warn("Failed to start microphone recording", {
                error: msg,
            });
            showToast.error("Couldn't access microphone", {
                id: "omnibar-mic-error",
            });
        });
    }, [recorder]);
    const handleCancelRecording = useCallback(() => {
        recorder.cancel();
    }, [recorder]);
    // Unmount-only cleanup for the staged list. The natural lifecycle
    // (`removeStagedAttachment` + the send path) handles mid-session
    // disposes; this effect is the safety net for "user closed the tab
    // mid-draft". Reads through the ref so the cleanup sees the latest
    // contents without re-firing on every list change.
    useEffect(() => {
        return () => {
            for (const att of stagedAttachmentsRef.current) {
                disposeStagedAttachment(att);
            }
        };
    }, []);
    const trimmed = text.trim();
    // Sendable when there's text OR staged attachments to commit. Voice
    // recording in flight blocks send — the user is mid-thought; we wait
    // for them to stop the take (which stages the chip) before allowing
    // the commit. Ask mode is unaffected by attachments; it's a
    // query-only surface (see the `mode === "add"` gate on
    // `<DraftAttachments>` below).
    const canSend = !isAsking &&
        recorder.isRecording === false &&
        (mode === "ask"
            ? trimmed.length > 0
            : trimmed.length > 0 || stagedAttachments.length > 0);
    // Mirror the live draft text onto the App store so other surfaces can
    // react to "what the user is typing" without prop-drilling. Powers the
    // home stream's live "filter Collections by title" mode (see
    // `StreamTabContent` in `app-screen.tsx`). Pushed on every keystroke
    // change rather than on submit, so the filter updates as you type;
    // cleared in the send / clear paths below by setting `text` to `""`.
    useEffect(() => {
        app.setOmnibarQuery(text);
    }, [app, text]);
    // ── Add-mode draft persistence ─────────────────────────────────────────
    //
    // Save the current Add-mode doc to localStorage on every editor
    // change, debounced so a burst of typing collapses into one write.
    // The `doc` state tracks the editor's content; this effect schedules
    // the save and tears the timer down on every change so only the
    // final keystroke in a burst commits. Submits + minimize callbacks
    // call `clearDraft("add")` synchronously so an in-flight pending
    // save won't clobber the cleared state.
    useEffect(() => {
        if (mode !== "add")
            return;
        if (doc === undefined || isEmptyTiptapDoc(doc)) {
            // An empty doc means the user cleared the bar — drop the
            // persisted entry rather than caching `{ type: "doc", content: [] }`
            // (keeps the storage tidy and means a fresh mount doesn't
            // restore an empty editor).
            clearDraft("add");
            return;
        }
        const t = window.setTimeout(() => saveDraft("add", doc), DRAFT_SAVE_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [doc, mode]);
    // Mount-time text seed: when we restored a doc from localStorage on
    // first render, the editor renders with that initialDoc but the
    // editor's onChange doesn't fire until the user actually edits. Mirror
    // the restored doc's plaintext into `text` so `canSend`, the home-
    // stream filter mirror, and the placeholder pulse all see the
    // restored content immediately. `initialDocFromStorage` is a
    // useMemo([])-stable value so this effect runs once per mount.
    useEffect(() => {
        if (initialDocFromStorage === undefined)
            return;
        setText(editorRef.current?.getText() ?? "");
    }, [initialDocFromStorage]);
    // On unmount, clear the published query so a route change away from
    // the omnibar's host doesn't leave a stale filter active on whatever
    // mounts next. Run-once cleanup keyed by `app` so React hot-reloads
    // pick up replacement instances without double-clearing.
    useEffect(() => {
        return () => {
            app.setOmnibarQuery("");
        };
    }, [app]);
    // True when the user has clicked the expand button. The pill
    // overrides its bottom-anchored layout into a vertically centered
    // focused panel (~600px wide, ~60% tall); the textarea is taken out
    // of the measure-and-set-pixel-height auto-grow loop and instead
    // fills the available vertical space via flex. Drives a
    // `data-expanded` attribute on the pill so the expanded layout can
    // be styled in SCSS without churning prop plumbing.
    const [isExpanded, setIsExpanded] = useState(false);
    // Mirror `isExpanded` into a ref so the bridge handle's `isExpanded()`
    // closure reads the live value without needing to re-register the
    // handle on every state flip. Re-registering on every render works
    // but churns the bridge's slot during typing-driven re-renders that
    // happen to also touch other state — the ref keeps the handle stable
    // across the bar's lifetime.
    const isExpandedRef = useRef(isExpanded);
    isExpandedRef.current = isExpanded;
    // Expose `isExpanded` through the bridge so the global `⌘D` cycle
    // (Add → Focus → Add → …) can both read the current state and flip
    // it without prop-drilling or lifting the state to the App store.
    // Registered once per bar mount with a stable handle; unregistered
    // on unmount so a stale closure can't fire against a torn-down
    // component (mirrors the `registerOmnibarMinimize` pattern below).
    useEffect(() => {
        registerOmnibarExpand({
            isExpanded: () => isExpandedRef.current,
            setExpanded: (value) => setIsExpanded(value),
        });
        return () => registerOmnibarExpand(undefined);
    }, []);
    // FLIP source rect — populated by `handleToggleExpand` on every
    // expand/collapse click. The layout effect below picks it up after
    // React commits the new layout (CSS has just snapped the bar from
    // resting → centered or vice versa) and replays the morph as a
    // `transform: translate + scale` from source → identity. Using
    // transform (rather than animating top/left/width/height) keeps the
    // animation off the layout thread and lets CSS continue owning the
    // resting + final rect declaratively. Trade-off: inner content is
    // briefly scaled during the transition (placeholder shrinks /
    // stretches as the pill grows / collapses); for a one-shot ~440ms
    // morph against a near-empty pill this reads as a satisfying bloom
    // rather than visible distortion.
    const flipSourceRectRef = useRef(null);
    // One-shot squeeze animation flag toggled when a block is sent. Cleared
    // by the `onAnimationEnd` handler on the bar element — using the real
    // animation-end event (rather than a `setTimeout` matching the keyframe
    // duration) avoids any timing drift that would cause a visible snap if
    // the modifier was removed before the animation actually finished.
    const [isSqueezing, setIsSqueezing] = useState(false);
    // One-shot "rising into place" flag for the bar's first paint. Lives on
    // a transient modifier so the base `.omnibar-draft` rule can keep
    // `animation: none` once mounted — otherwise the squeeze class's
    // `animation: omnibar-squeeze` override on send would, on removal, revert
    // the animation property back to `omnibar-rise` and the CSS animation
    // engine would replay rise from `opacity: 0`, flashing the bar after
    // every send. Cleared shortly after the rise's keyframe duration (220ms)
    // so the modifier is gone well before any user interaction can land.
    const [isMounting, setIsMounting] = useState(true);
    useEffect(() => {
        const t = window.setTimeout(() => setIsMounting(false), 280);
        return () => window.clearTimeout(t);
    }, []);
    // Bridge registration. Picks the right backing per mode so the
    // ⌘D/⌘F shortcuts target whichever surface is currently mounted.
    // Re-runs on `mode` change since switching modes swaps which
    // element is in the DOM.
    //
    // Registers a STABLE wrapper, not a snapshot. The wrapper reads
    // `editorRef.current` (or the textarea ref) at call time so a
    // mid-session swap of the underlying handle is transparent to the
    // bridge. This matters concretely because `<RichTextEditor>`'s Tiptap
    // editor is rebuilt whenever its `extensions` array changes — and
    // `extensions` is memoised on `placeholder`, which embeds the
    // destination chip's label (`"Add to ${chipLabel}..."`). Navigating
    // between Collections updates the active-collection chip, which
    // updates the placeholder, which destroys the current editor and
    // creates a fresh one. `useImperativeHandle` writes the new
    // `RichTextEditorHandle` to `editorRef.current` in the same commit, but
    // this `useEffect` only re-runs on `[mode]` change — not on editor
    // rebuild. A snapshot registered here would leave the bridge holding
    // a handle that closes over the now-destroyed editor; ⌘D would call
    // `editor.commands.focus("end")` on the zombie, which silently
    // no-ops (the destroyed view's DOM is no longer connected). The
    // wrapper indirection makes the bridge always see the live handle.
    //
    // No `[mode]`-effect cleanup here, on purpose: the bridge holds a
    // single handle slot and the next register() call overwrites it,
    // so the in-between `register(undefined)` a cleanup would issue is
    // both redundant AND would clobber the bridge's
    // `focusOnNextRegister` pending flag — the very mechanism that
    // keeps `⌘D`/`⌘F` (and the on-click mode toggle) focused across
    // the textarea ↔ editor remount. The companion `[]`-keyed effect
    // below handles the true unmount case.
    useEffect(() => {
        if (mode === "add") {
            registerOmnibarInput({
                focus: () => editorRef.current?.focus(),
                selectAll: () => editorRef.current?.selectAll(),
                isEmpty: () => editorRef.current?.isEmpty() ?? true,
                isFocused: () => editorRef.current?.isFocused() ?? false,
            });
        }
        else {
            // Textarea path uses the same indirection so the contract is
            // symmetric. In practice the textarea element ref is stable
            // across renders (it's a plain DOM node that React reconciles
            // in place), but reading `textareaRef.current` at call time
            // means a future refactor that swaps the underlying element
            // won't reintroduce the same stale-handle bug.
            registerOmnibarInput({
                focus: () => textareaRef.current?.focus(),
                selectAll: () => textareaRef.current?.select(),
                isEmpty: () => (textareaRef.current?.value.length ?? 0) === 0,
                isFocused: () => {
                    const el = textareaRef.current;
                    if (!el)
                        return false;
                    const doc = el.ownerDocument ?? document;
                    return doc.activeElement === el;
                },
            });
        }
    }, [mode]);
    // Unmount-only cleanup. Keeps the bridge's stored handle in sync
    // with the bar's mount state without firing on every `[mode]` swap
    // — see the rationale on the registration effect above.
    useEffect(() => {
        return () => registerOmnibarInput(undefined);
    }, []);
    // Expose a "tear down the bar" callback to siblings inside the host
    // (specifically the filter strip when a result is activated). Clearing
    // local `text` cascades through the existing mirror effect to set
    // `omnibarQuery=""`, which makes `OmnibarHost` unmount the strip and
    // collapse the rise gradient. We also blur whatever's focused inside
    // the host so focus doesn't end up orphaned on a node about to
    // unmount, and reset stream-fade explicitly because focus may have
    // been on a result card (not the textarea), so the textarea's
    // `onBlur` never fired. Finally, flip the mode back to `add` — the
    // only path that triggers minimize today is the Ask-mode filter
    // strip's "result activated" hook, and the user's next interaction
    // with the bar after landing on a Collection is overwhelmingly
    // "capture something here", not "run another search". Returning to
    // Add (Draft) state matches that intent and keeps Ask scoped to the
    // explicit ⌘F entry rather than a sticky mode the user has to
    // manually exit.
    useEffect(() => {
        registerOmnibarMinimize(() => {
            setText("");
            // Tear down the rich-text editor's content too — clearing only
            // `text` would leave the editor's internal state holding the
            // composed doc and the next mount would resurrect it via the
            // empty-doc-skips-save guard. `clear()` also fires onChange so
            // the persistence effect runs `clearDraft("add")` for free.
            editorRef.current?.clear();
            setDoc(undefined);
            // Cancel any in-flight voice recording so the mic indicator
            // clears immediately, and drop every staged attachment so the
            // bar comes back empty next time the user engages it. Using the
            // ref's current value (rather than `stagedAttachments` from the
            // closure) means we always dispose the freshest list — the
            // minimize handler is registered once per app instance, so a
            // closure capture would dispose a stale snapshot.
            recorder.cancel();
            for (const att of stagedAttachmentsRef.current) {
                disposeStagedAttachment(att);
            }
            setStagedAttachments([]);
            const active = document.activeElement;
            if (active instanceof HTMLElement && active.closest(".omnibar-host")) {
                active.blur();
            }
            app.setStreamFadeOpacity(STREAM_FADE_OPACITY_IDLE);
            app.setOmnibarFocused(false);
            app.setOmnibarMode("add");
        });
        return () => registerOmnibarMinimize(undefined);
    }, [app, recorder]);
    // While the draft is active (textarea focus, focus moved to a child
    // control, or expanded canvas), a pointerdown on the stream would
    // otherwise reach block hit-targets in the bubble phase. Capture early,
    // dismiss omnibar chrome only, and swallow the event so cards don't
    // open. Radix destination popover (portal `.popover`), overlay
    // scrollbars, and dialogs keep working — they're excluded here.
    useEffect(() => {
        const onPointerDownCapture = (e) => {
            if (e.button !== 0)
                return;
            const root = draftRootRef.current;
            if (!root)
                return;
            const draftHoldsFocus = root.matches(":focus-within");
            const omnibarIsActive = isExpanded || draftHoldsFocus;
            if (omnibarIsActive === false)
                return;
            const target = e.target;
            if (!(target instanceof Node))
                return;
            if (root.contains(target))
                return;
            if (target instanceof Element) {
                if (target.closest(".popover"))
                    return;
                if (target.closest('[role="dialog"]'))
                    return;
                if (target.closest(".os-scrollbar"))
                    return;
                // The Ask-mode filter strip is a sibling of `DraftBar` inside
                // `OmnibarHost` — its cards live outside this root but are
                // logically part of the bar's interaction surface. Without
                // this exemption the capture handler `preventDefault`s the
                // pointerdown on a result card, which cancels the subsequent
                // click and stops `<CollectionBlockView />` from navigating.
                if (target.closest(".omnibar-filter-strip"))
                    return;
                // Sonner's toast pill (used here for the "click to collapse"
                // hint while expanded). Capture-phase `stopPropagation` would
                // break Sonner's own pointerdown plumbing — swipe-to-dismiss
                // and the visible "press" state both rely on it. Exempting
                // the toaster keeps the hint dismissible without re-firing
                // the click-outside path against itself.
                if (target.closest("[data-sonner-toaster]"))
                    return;
            }
            e.preventDefault();
            e.stopPropagation();
            if (isPickerOpen) {
                setIsPickerOpen(false);
            }
            if (isExpanded) {
                // Expansion is a deliberate "I'm writing" posture, so an
                // accidental click on the scrim shouldn't dismiss it. Surface
                // an explicit "Exit Focus Mode" CTA on the toast instead —
                // one-click escape hatch that runs the same `setIsExpanded(false)`
                // path as the in-pill collapse button. A stable `id` lets repeat
                // outside clicks update the same pill in place rather than
                // stacking duplicates. Focus + stream fade stay put so the
                // editor remains the active surface; clicking the action
                // dismisses the toast (Sonner default).
                showToast.neutral("Focus Mode", {
                    id: "omnibar-collapse-hint",
                    action: {
                        label: "Exit Focus Mode",
                        onClick: () => setIsExpanded(false),
                    },
                });
                return;
            }
            const active = document.activeElement;
            if (active instanceof HTMLElement && root.contains(active)) {
                active.blur();
            }
            // `onBlur` on the textarea restores fade; Send / other inner controls
            // don't — sync explicitly so the stream never stays dimmed. Same
            // defensive sync for the omnibar-focused flag so the stack-
            // isolation dim doesn't get stuck when the dismiss lands on focus
            // that lives outside the input (a chip, the destination popover).
            app.setStreamFadeOpacity(STREAM_FADE_OPACITY_IDLE);
            app.setOmnibarFocused(false);
        };
        document.addEventListener("pointerdown", onPointerDownCapture, true);
        return () => document.removeEventListener("pointerdown", onPointerDownCapture, true);
    }, [app, isExpanded, isPickerOpen]);
    // Pasting from the system clipboard while no editable target has
    // focus drops the clipboard contents into the draft and focuses the
    // active surface — the omnibar acts as the catch-all destination for
    // ad-hoc pastes, no need to click into the pill first. Skips when
    // focus is already in an input/textarea/contenteditable (native
    // paste / the editor's own `handlePaste` belong there) and when a
    // dialog is open (paste belongs to the modal).
    //
    // Two branches:
    //   - File(s) in the clipboard (image, PDF, archive, voice memo,
    //     anything with `kind === "file"`): stage them via
    //     `uploadPastedFiles`. Always lands in Add mode — attachments
    //     only render in Add mode (the chip row is gated by
    //     `mode === "add"`), so if the user happened to be in Ask we
    //     swap them over so the freshly-staged chip is actually
    //     visible. Either way we focus the editor afterwards so the
    //     user can immediately type a caption alongside the
    //     attachment without re-clicking the pill.
    //   - Plain text: routes to the active surface based on `mode` — Add
    //     appends into the Tiptap editor (single-paragraph re-set; see
    //     the TODO below), Ask appends into the textarea state via
    //     `setText`. Both branches focus the surface and place the caret
    //     at the end so the user can keep typing without re-clicking.
    useEffect(() => {
        const onDocumentPaste = (e) => {
            const active = document.activeElement;
            const targetIsEditable = active instanceof HTMLElement &&
                (active.tagName === "INPUT" ||
                    active.tagName === "TEXTAREA" ||
                    active.isContentEditable);
            if (targetIsEditable)
                return;
            if (document.querySelector('[role="dialog"]'))
                return;
            // File branch first — a clipboard carrying both files and text
            // (Notion screenshot copy bundles OCR'd text alongside the
            // image, Finder's "copy file" path can include a path mirror)
            // reads as file-primary; the text mirror is ignored. Matches
            // the `handlePaste` rule in the editor so behaviour is identical
            // inside vs. outside the contenteditable.
            const pastedFiles = extractFiles(e.clipboardData);
            if (pastedFiles.length > 0) {
                e.preventDefault();
                uploadPastedFiles(pastedFiles);
                // Attachments only render in Add mode (the chip row is gated
                // on `mode === "add"`), so swap there if we landed via Ask
                // before kicking focus over. When already in Add mode we
                // focus the live handle directly; when switching, the bridge's
                // pending-register slot consumes the focus on the next
                // handle registration (driven by draft-bar's `[mode]`-keyed
                // registration effect), avoiding a focus-then-unmount race
                // as the textarea ↔ editor swap.
                if (app.state.ui.omnibarMode !== "add") {
                    app.setOmnibarMode("add");
                    focusOmnibarInputOnNextRegister();
                }
                else {
                    focusOmnibarInput();
                }
                return;
            }
            const pasted = e.clipboardData?.getData("text/plain") ?? "";
            if (pasted.length === 0)
                return;
            e.preventDefault();
            if (mode === "add") {
                // Editor handle is registered on mount; focus + insert via
                // Tiptap's command surface so the paste plays through ProseMirror's
                // normal pipeline (input rules, history, etc.) rather than
                // bypassing it via raw DOM manipulation. The editor's onChange
                // fires synchronously after insertContent so `text` + `doc` get
                // mirrored back into React state without an extra rAF.
                editorRef.current?.focus();
                // Routing through `commands.insertContent` is handled inside
                // `editorRef`'s update path — see RichTextEditor for the wiring.
                // Construct a single-line plaintext insertion; the editor
                // replaces selection (none → caret) with the pasted string.
                // Defer to next frame so the focus + insert sequence runs after
                // any in-flight React commit, mirroring the textarea branch.
                const pastedText = pasted;
                requestAnimationFrame(() => {
                    // Read the current text via the imperative handle so we know
                    // whether to prepend a newline separator (matches the Ask
                    // branch's "preserve in-flight draft" behaviour).
                    const editor = editorRef.current;
                    if (!editor)
                        return;
                    const currentText = editor.getText();
                    const insertion = currentText.length === 0 ? pastedText : `\n${pastedText}`;
                    // Tiptap doesn't expose a generic "insert" on the imperative
                    // handle today (we'd have to widen RichTextEditorHandle to do
                    // that cleanly). For v1, fall back to setting the editor's
                    // content as a single paragraph carrying the merged text —
                    // formatting on existing content is lost, which is an
                    // accepted trade-off for the catch-all paste path.
                    // TODO: widen RichTextEditorHandle with `insertText` to keep
                    // existing marks intact when pasting into an in-flight draft.
                    const merged = currentText + insertion;
                    editor.setDoc({
                        type: "doc",
                        content: [{ type: "paragraph", content: [{ type: "text", text: merged }] }],
                    });
                    // setDoc with `emitUpdate: false` skips onChange; mirror
                    // `text` (and `doc`) into React state explicitly so the home-
                    // stream filter and persistence effect both pick up the
                    // change.
                    setText(merged);
                    setDoc(editor.getDoc());
                    editor.focus();
                });
                return;
            }
            // Ask mode — textarea-backed branch (existing behaviour).
            setText((prev) => (prev.length === 0 ? pasted : `${prev}\n${pasted}`));
            // React commits the new state synchronously before paint; rAF
            // gives the live textarea a chance to pick up the new value
            // (and the auto-grow layout effect to set its height) before we
            // place the caret at the end and focus.
            requestAnimationFrame(() => {
                const el = textareaRef.current;
                if (!el)
                    return;
                el.focus();
                const end = el.value.length;
                el.setSelectionRange(end, end);
            });
        };
        document.addEventListener("paste", onDocumentPaste);
        return () => document.removeEventListener("paste", onDocumentPaste);
    }, [app, mode, uploadPastedFiles]);
    // Caret-into-view helper for the Ask-mode textarea. The textarea is
    // wrapped in `<ScrollView>` and is sized to its full natural content
    // height (see auto-grow effect below) — meaning the textarea itself
    // never scrolls internally, the surrounding ScrollView does. When
    // the user types past the visible region, browsers' built-in caret-
    // tracking has nothing to scroll inside the textarea, so we forward
    // that responsibility here: after every text change, if the caret is
    // sitting below the viewport's visible bottom, scroll the viewport
    // down to bring it back into view.
    //
    // Heuristic by newline-count rather than mirror-based pixel
    // measurement: counts hard `\n` line breaks before the caret to
    // figure out which visual line the caret is on. This is correct for
    // multi-line drafts entered via `Shift+Enter` and a reasonable
    // approximation for soft-wrapped content (which is rare in Ask mode
    // — queries are generally short). The user can scroll the ScrollView
    // manually for the soft-wrap case if it ever drifts.
    const ensureCaretVisible = useCallback(() => {
        const el = textareaRef.current;
        const viewport = textareaScrollRef.current?.osInstance()?.elements().viewport;
        if (!el || !viewport)
            return;
        const caretIndex = el.selectionStart ?? el.value.length;
        const valueUpToCaret = el.value.substring(0, caretIndex);
        const linesBeforeCaret = valueUpToCaret.split("\n").length - 1;
        const caretTop = TEXTAREA_PADDING_TOP + linesBeforeCaret * TEXTAREA_LINE_HEIGHT;
        const caretBottom = caretTop + TEXTAREA_LINE_HEIGHT;
        const scrollTop = viewport.scrollTop;
        const visibleBottom = scrollTop + viewport.clientHeight;
        if (caretBottom > visibleBottom) {
            viewport.scrollTop = caretBottom - viewport.clientHeight;
        }
        else if (caretTop < scrollTop) {
            viewport.scrollTop = Math.max(0, caretTop);
        }
    }, []);
    // Auto-grow: read the mirror textarea's `scrollHeight` (which always
    // reflects natural content height because the mirror is pinned at
    // `height: 0` in CSS) and copy that pixel value to the live
    // textarea. The live textarea's height only moves between pixel
    // targets, so its `transition: height` runs cleanly. The 6-line
    // visible cap lives on the surrounding `<ScrollView>` wrapper as
    // `max-height: 160px` in `.omnibar-draft__textarea-scroll`; the
    // textarea itself is always sized to its full content height so the
    // ScrollView's overlay scrollbar takes over once content exceeds the
    // cap (rather than the textarea's native thin scrollbar, which
    // hugged the pill's inner edge before this wrapper went in).
    //
    // Ask mode only — Add mode renders a Tiptap contenteditable that
    // auto-grows naturally inside its own `<ScrollView>` (see
    // `@/components/rich-text/rich-text-editor.tsx`).
    useLayoutEffect(() => {
        if (mode !== "ask")
            return;
        const el = textareaRef.current;
        const mirror = mirrorRef.current;
        if (!el || !mirror)
            return;
        // In expanded mode the textarea is sized by the flex layout, not
        // by the auto-grow loop. Clearing the inline height hands control
        // back to CSS so `flex: 1 1 auto` + `height: 100%` can fill the
        // pill.
        if (isExpanded) {
            el.style.height = "";
            return;
        }
        el.style.height = `${mirror.scrollHeight}px`;
        ensureCaretVisible();
    }, [text, isExpanded, mode, ensureCaretVisible]);
    // Re-measure on window resize since the textarea's wrapping width
    // changes with the slate. The mirror is anchored to the same
    // horizontal slot as the live textarea (see SCSS) so its
    // `scrollHeight` automatically reflects the new wrapping. Ask mode
    // only (see auto-grow effect above for the rationale).
    useEffect(() => {
        if (mode !== "ask")
            return;
        const onResize = () => {
            const el = textareaRef.current;
            const mirror = mirrorRef.current;
            if (!el || !mirror)
                return;
            if (isExpanded)
                return; // CSS controls height in expanded mode
            el.style.height = `${mirror.scrollHeight}px`;
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [isExpanded, mode]);
    const handleFocus = useCallback(() => {
        app.setStreamFadeOpacity(STREAM_FADE_OPACITY_FOCUSED);
        app.setOmnibarFocused(true);
        setIsFocused(true);
    }, [app]);
    const handleBlur = useCallback(() => {
        app.setStreamFadeOpacity(STREAM_FADE_OPACITY_IDLE);
        app.setOmnibarFocused(false);
        setIsFocused(false);
    }, [app]);
    // Ask-mode focus → select-all on existing content, address-bar style.
    // Mirrors the same convention `⌘F` already uses on second press
    // (see `focusAndSelectOmnibarInput` in `omnibar-bridge.ts`) and
    // extends it to every focus path: click, Tab, programmatic. The
    // user's intent in Ask mode is "I'm coming back to revise my query",
    // and selecting the existing text lets them immediately overtype
    // rather than appending to it.
    //
    // Add mode keeps its caret-preserving behaviour — the Add surface is
    // for extending an in-flight capture, where a select-all on focus
    // would be destructive (the very rationale called out in
    // `use-omnibar-focus-shortcut.ts`'s `⌘D` comment block). So this
    // handler is wired to the textarea only; the editor's `onFocus`
    // continues to use `handleFocus` directly.
    const handleAskFocus = useCallback((e) => {
        handleFocus();
        if (e.currentTarget.value.length > 0) {
            e.currentTarget.select();
        }
    }, [handleFocus]);
    // Click-from-blurred path needs special handling because the native
    // mousedown sequence is: mousedown → focus → mouseup → click, and
    // mouseup unconditionally collapses any selection (including one we
    // set in onFocus) back to a zero-width caret at the click point.
    // Calling `select()` from onFocus alone therefore "works" for Tab
    // and programmatic focus but appears to do nothing on click.
    //
    // The fix is to short-circuit the native mousedown on the
    // blurred-and-non-empty case: preventDefault stops the browser from
    // initiating its own focus/selection sequence, then `el.focus()`
    // drives the focus event imperatively. `handleAskFocus` runs as a
    // result and calls `select()`, which now sticks because there is no
    // subsequent mouseup-driven caret reset.
    //
    // Two early-outs keep native behaviour intact where it's wanted:
    //   - Already focused: clicking inside the text mid-edit should
    //     position the caret normally, not blow away the user's spot.
    //   - Empty input: nothing to select-all, let the native focus +
    //     caret-at-zero behaviour run untouched.
    const handleAskMouseDown = useCallback((e) => {
        const el = e.currentTarget;
        if (document.activeElement === el)
            return;
        if (el.value.length === 0)
            return;
        e.preventDefault();
        el.focus();
    }, []);
    const handleAddSend = useCallback(() => {
        // In Add mode the editor is the source of truth for the doc and
        // the canonical plaintext (Tiptap's `getText()` produces a
        // block-separator-aware string). Read both off the imperative
        // handle rather than the React-state mirrors so the send always
        // ships exactly what's currently visible in the editor — even
        // if a transaction is in flight that hasn't synced into `text`/
        // `doc` yet.
        const editor = editorRef.current;
        const content = editor ? editor.getText().trim() : trimmed;
        const docToSend = editor ? editor.getDoc() : doc;
        const hasText = content.length > 0;
        const hasAttachments = stagedAttachments.length > 0;
        if (hasText === false && hasAttachments === false)
            return;
        const collectionIds = Array.from(selectedCollectionIds);
        const target = collectionIds.length === 0
            ? "stream"
            : collectionIds.length === 1
                ? "single_collection"
                : "multiple_collections";
        // One block per staged attachment so each lands in the stream as
        // its own card. This matches today's behaviour for drag-drop
        // (each file = one block) and for clipboard paste (one image =
        // one block), so saved memories keep their file-per-card grain
        // regardless of input method.
        //
        // `lastInsertedBlockId` captures the id of the most recently
        // inserted placeholder — `mainStreamStore.prependCaptured` lands
        // each new id at the top of the stream in call order, so the last
        // one wins and is what we want to scroll into view after submit.
        let lastInsertedBlockId;
        const captureInserted = (id) => {
            lastInsertedBlockId = id;
        };
        for (const attachment of stagedAttachments) {
            void blockStore.add({ files: [attachment.file], collectionIds }, { onOptimisticInsert: captureInserted });
            track({
                name: "block.added",
                props: {
                    input_method: "omnibar",
                    content_kind: "file",
                    file_type: attachment.file.type || undefined,
                    target,
                    collection_count: collectionIds.length,
                },
            });
        }
        // Text caption (if any) is a separate block. We deliberately
        // don't merge it into the attachment block today — the text-block
        // schema and the file-block schema are distinct in `block-store`,
        // so a "caption + image" merger would need richer server-side
        // support than v1 has. The user-visible effect: a text+image
        // capture appears as two adjacent cards in the stream.
        if (hasText) {
            void blockStore.add({ content, doc: docToSend, collectionIds }, { onOptimisticInsert: captureInserted });
            track({
                name: "block.added",
                props: {
                    input_method: "omnibar",
                    content_kind: "text",
                    target,
                    collection_count: collectionIds.length,
                },
            });
        }
        if (lastInsertedBlockId !== undefined) {
            blockStore.scrollBlockIntoView(lastInsertedBlockId);
        }
        // Dispose any object URLs we own (image previews) before clearing
        // the staged list — once the blob has been handed to
        // `blockStore.add` the preview URL is no longer needed.
        for (const attachment of stagedAttachments) {
            disposeStagedAttachment(attachment);
        }
        setStagedAttachments([]);
        // Clear local state + the editor's content + the persisted draft.
        // `editor.clear()` fires onChange with an empty doc; the persistence
        // effect's empty-doc guard then runs `clearDraft("add")`. We also
        // call `setDoc(undefined)` + `setText("")` directly so the React
        // state mirrors update synchronously instead of waiting for the
        // editor's onChange callback to roundtrip.
        editor?.clear();
        setText("");
        setDoc(undefined);
        clearDraft("add");
        // Keep focus on the editor after sending so the user can rapid-fire
        // additional captures without re-clicking the pill. On desktop the
        // editor is already focused; the explicit `focus()` is a defensive
        // re-focus for the rare case where the send button's pointerdown
        // path stole focus despite `preserveFocus`.
        editor?.focus();
        // Focus Mode (the expanded panel) is a deliberate "I'm composing
        // this in detail" posture for a SINGLE capture — once the send
        // lands, always drop out so the freshly-added card in the stream
        // below isn't hidden behind the panel. The FLIP layout effect
        // picks up `flipSourceRectRef` and morphs the panel back to resting
        // in lockstep with the `setIsExpanded(false)` commit.
        const wasExpanded = isExpanded;
        if (wasExpanded) {
            const root = draftRootRef.current;
            flipSourceRectRef.current = root ? root.getBoundingClientRect() : null;
            setIsExpanded(false);
        }
        // Squeeze animation is the visual "send + tuck away" gesture that
        // masks the bar's collapse from focused (400px + meta row) back to
        // its 240px resting state. Skipped when collapsing out of expanded
        // mode: the FLIP morph back to resting is doing the heavy visual
        // lifting, and the squeeze's `transform: scale()` keyframe plus
        // its `width: 400px` override would fight both the FLIP and the
        // resting 240px target.
        if (wasExpanded === false) {
            // Re-trigger the squeeze animation on rapid successive sends by
            // toggling the flag off and back on across paint frames.
            setIsSqueezing(false);
            requestAnimationFrame(() => {
                setIsSqueezing(true);
            });
        }
    }, [trimmed, doc, stagedAttachments, selectedCollectionIds, isExpanded]);
    const handleAskSend = useCallback(async () => {
        const content = trimmed;
        if (content.length === 0 || isAsking)
            return;
        track({
            name: "assistant.queried",
            props: {
                query_length_bucket: queryLengthBucket(content),
                has_attachments: false,
            },
        });
        const transientId = newTransientId();
        const optimisticTitle = content.length > 40 ? `${content.slice(0, 37)}…` : content;
        // Open the transient tab immediately with skeleton state so the user
        // sees the response surface before the network round-trip lands.
        startQuery(transientId, content);
        openCollectionTab({ collectionId: transientId, title: optimisticTitle });
        navigate(urlForMemberOnlyEntity(transientId));
        setText("");
        textareaRef.current?.blur();
        setIsAsking(true);
        try {
            const result = await sendAssistantQuery(content);
            setQueryReady(transientId, result);
            // Refresh tab title to the assistant-chosen collection name.
            openCollectionTab({ collectionId: transientId, title: result.name });
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : "Something went wrong";
            draftBarLogger.warn("sendAssistantQuery failed", { error: msg });
            setQueryError(transientId, msg);
        }
        finally {
            setIsAsking(false);
        }
    }, [trimmed, isAsking, navigate]);
    const handleSend = useCallback(() => {
        if (mode === "ask") {
            void handleAskSend();
            return;
        }
        handleAddSend();
    }, [mode, handleAddSend, handleAskSend]);
    const handleAnimationEnd = useCallback((e) => {
        if (e.animationName.includes("omnibar-squeeze")) {
            setIsSqueezing(false);
        }
    }, []);
    const handleToggleExpand = useCallback(() => {
        // Capture whether the user was actively drafting before the toggle —
        // if so, restore focus on the textarea after the layout flips so
        // keyboard input continues uninterrupted. Suppressing the
        // mousedown's default on the button itself means focus doesn't
        // bounce to the button mid-flip.
        // "Was the user actively drafting?" check works against either
        // backing — for the textarea, focus is on the element itself; for
        // the editor it's on the rendered ProseMirror contenteditable
        // descendant. `:focus-within` on the bar root is the cheapest
        // common signal across both.
        const root = draftRootRef.current;
        const wasFocused = typeof document !== "undefined" && root?.matches(":focus-within") === true;
        // Stash the bar's current rect so the FLIP layout effect can
        // replay the morph after React commits the new layout. Captured in
        // viewport coords; the effect converts to "delta from new rect"
        // before applying the inverse transform.
        flipSourceRectRef.current = root ? root.getBoundingClientRect() : null;
        setIsExpanded((prev) => !prev);
        if (wasFocused) {
            // Restore focus on the active mode's surface — `mode` is captured
            // in the closure here but useCallback's deps include `mode` so
            // the right backing is targeted for the current state.
            requestAnimationFrame(() => {
                if (mode === "add") {
                    editorRef.current?.focus();
                }
                else {
                    textareaRef.current?.focus();
                }
            });
        }
    }, [mode]);
    // FLIP morph for expand/collapse. Runs after every `isExpanded`
    // change, but only does work when `flipSourceRectRef` was populated
    // by an interactive toggle (the very first render skips because the
    // ref is null). Uses transform-based FLIP — translate + scale to the
    // source rect on the same frame React paints, then animate back to
    // identity over ~440ms. The animation cleanup removes inline styles
    // so subsequent CSS state changes (focus-within widening, squeeze
    // animation, etc.) aren't shadowed by stale inline values.
    useLayoutEffect(() => {
        const source = flipSourceRectRef.current;
        if (!source)
            return undefined;
        flipSourceRectRef.current = null;
        const el = draftRootRef.current;
        if (!el)
            return undefined;
        // After React's commit, CSS has applied the new rest/expanded
        // styles — measure that target rect now so the inverse transform
        // lands exactly on the previous source rect.
        const finalRect = el.getBoundingClientRect();
        const dx = source.left - finalRect.left;
        const dy = source.top - finalRect.top;
        const sx = source.width / Math.max(1, finalRect.width);
        const sy = source.height / Math.max(1, finalRect.height);
        // Bail if there's effectively nothing to animate (e.g. resize
        // happened during the toggle and rects coincidentally match) so
        // we don't override the bar's CSS transform-origin for nothing.
        if (Math.abs(dx) < 0.5 &&
            Math.abs(dy) < 0.5 &&
            Math.abs(sx - 1) < 0.01 &&
            Math.abs(sy - 1) < 0.01) {
            return undefined;
        }
        const ANIM_MS = 440;
        // Pre-animation frame: snap to the source rect with no transition
        // so the user sees the bar at its previous position. Override the
        // base `transform-origin: 50% 100%` (used by the squeeze animation)
        // to top-left so the translate + scale composes cleanly from the
        // upper-left corner — matches how `getBoundingClientRect`'s
        // top/left coordinates align with the inverse delta.
        el.style.transition = "none";
        el.style.transformOrigin = "top left";
        el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
        // Force a style flush so the browser doesn't coalesce the snap +
        // unsnap into a single paint and drop the transition entirely.
        void el.offsetHeight;
        // Two RAFs: first to ensure the snap has actually painted, second
        // to apply the transition + remove the transform. Skipping the
        // second RAF works on Chrome but Safari occasionally re-merges the
        // two style writes into one paint frame, dropping the transition.
        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                el.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`;
                el.style.transform = "";
            });
        });
        // Belt-and-suspenders cleanup: even though `transitionend` would
        // fire, a parallel state change (mode toggle, send squeeze) could
        // cancel the transition midway and leave the inline transform
        // dangling. The timeout guarantees inline styles get torn down so
        // the bar always returns to fully CSS-driven layout.
        const cleanupTimer = window.setTimeout(() => {
            el.style.transition = "";
            el.style.transformOrigin = "";
            el.style.transform = "";
        }, ANIM_MS + 80);
        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            window.clearTimeout(cleanupTimer);
            el.style.transition = "";
            el.style.transformOrigin = "";
            el.style.transform = "";
        };
    }, [isExpanded]);
    // Keyboard handler shared between the textarea (Ask) and the editor
    // (Add). The handler runs against either backing — Tiptap's onKeyDown
    // bubbles through `EditorContent`'s wrapping div, the textarea fires
    // it natively. Both branches share the same Escape/Tab/Enter logic,
    // diverging only on which backing's `blur()` to call when Escape
    // dismisses focus.
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Escape") {
            // Collapse first if expanded, then blur on the next Escape so
            // the user always has a way out without losing focus mid-edit.
            if (isExpanded) {
                setIsExpanded(false);
                return;
            }
            // Route blur through the right backing — Tiptap's `commands.blur`
            // tears down the editor's focus state cleanly (just calling
            // `e.currentTarget.blur()` would blur the wrapping div, which
            // for Tiptap doesn't dismiss the contenteditable).
            if (mode === "add") {
                editorRef.current?.blur();
            }
            else {
                textareaRef.current?.blur();
            }
            return;
        }
        // Tab in Ask mode with a non-empty query and at least one real
        // (non-placeholder) result card visible → focus the first card.
        // Everything else (Add mode, empty Ask query, no real results,
        // Shift+Tab) falls through to the browser's native focus
        // traversal — there is no app-level Tab cycle. Mode toggling is
        // reserved for the explicit toggle buttons / `⌘D` / `⌘F`
        // shortcuts; it's not bound to Tab.
        if (e.key === "Tab" &&
            e.shiftKey === false &&
            e.metaKey === false &&
            e.ctrlKey === false &&
            e.altKey === false &&
            mode === "ask" &&
            trimmed.length > 0) {
            const firstCard = document.querySelector(".omnibar-filter-strip .omnibar-filter-strip__cell:not(.omnibar-filter-strip__cell--placeholder) button");
            if (firstCard) {
                // preventDefault suppresses native focus traversal so the
                // browser doesn't move focus to the next focusable element
                // in the document while we're routing it explicitly.
                e.preventDefault();
                firstCard.focus();
                return;
            }
        }
        // Submit shortcut differs by mode:
        //   - Add: `⌘/Ctrl+Enter` submits; plain Enter inserts a newline
        //     (or a list-item, etc., depending on the editor's current
        //     node), so multi-line / structured captures don't
        //     accidentally commit on Return.
        //   - Ask: plain Enter prefers the top quick-result when the
        //     filter strip has at least one instant match, mirroring the
        //     address-bar / command-palette convention where the
        //     highlighted-by-default first row is the natural Enter
        //     target. Only when there's nothing to open does it fall
        //     through to `sendAssistantQuery`. `Shift+Enter` still
        //     inserts a newline for the rare multi-line query.
        if (e.key === "Enter") {
            if (mode === "ask") {
                if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey)
                    return;
                e.preventDefault();
                if (openOmnibarFirstResult())
                    return;
                handleSend();
                return;
            }
            if (e.metaKey || e.ctrlKey) {
                e.preventDefault();
                handleSend();
            }
        }
    }, [handleSend, isExpanded, mode, trimmed]);
    const draftClassName = [
        "omnibar-draft",
        isMounting ? "omnibar-draft--mounting" : "",
        isSqueezing ? "omnibar-draft--squeezing" : "",
    ]
        .filter(Boolean)
        .join(" ");
    // Placeholder for the editor / textarea. While voice recording is
    // in progress the input row is overlaid with the live-waveform UI
    // (see the `<LiveWaveform>` mounted below) — drop the placeholder
    // so its text doesn't bleed through behind the overlay's bars on
    // platforms where the contenteditable's `is-editor-empty` pseudo
    // sits in the same paint layer.
    const placeholder = recorder.isRecording
        ? ""
        : mode === "ask"
            ? "Ask anything..."
            : `Add to ${chipLabel}...`;
    // True whenever the pill is "in active use" — focus inside the
    // textarea / editor or the destination picker open. Surfaced as
    // `data-active` on the wrapping div so cross-subtree CSS rules
    // (the viewer toaster's lift in `app.scss`) can react to omnibar
    // state via a reliably-invalidating attribute selector. Focus
    // Mode (`isExpanded`) deliberately stays out of this union — the
    // toast is meant to stay pinned to the viewport bottom there, see
    // the comment block in `app.scss` next to the lift rule.
    const isActive = isFocused || isPickerOpen;
    const destinationDecoration = useMemo(() => _jsx(WipBorder, {}), []);
    const destinationFilter = useMemo(() => ({
        value: collectionFilter,
        onChange: setCollectionFilter,
        placeholder: "Filter collections…",
    }), [collectionFilter]);
    return (_jsxs("div", { ref: draftRootRef, className: draftClassName, role: "presentation", onAnimationEnd: handleAnimationEnd, "data-has-text": trimmed.length > 0 ? "" : undefined, "data-expanded": isExpanded ? "" : undefined, "data-mode": mode, "data-mode-changing": isModeChanging ? "" : undefined, "data-active": isActive ? "" : undefined, "data-recording": recorder.isRecording ? "" : undefined, "data-has-attachments": stagedAttachments.length > 0 ? "" : undefined, children: [_jsx("div", { className: "omnibar-draft__wash", "aria-hidden": "true" }), mode === "add" && stagedAttachments.length > 0 ? (_jsx("div", { className: "omnibar-draft__chip-row", role: "list", "aria-label": "Staged attachments", children: stagedAttachments.map((attachment) => (_jsx(DraftAttachmentChip, { attachment: attachment, onRemove: removeStagedAttachment }, attachment.id))) })) : null, _jsxs("div", { className: "omnibar-draft__row omnibar-draft__row--input", children: [recorder.isRecording ? (_jsx(LiveWaveform, { levels: recorder.liveLevels, durationSeconds: recorder.durationSeconds, onCancel: handleCancelRecording })) : null, mode === "add" ? (
                    // Add mode: rich-text Tiptap editor. The contenteditable's
                    // class is `omnibar-draft__input card-content--rich`:
                    //   • `omnibar-draft__input` carries the bar-specific chrome
                    //     (focus ring, expanded morph, typewriter fade, pulse,
                    //     editor typography/padding) so all existing host SCSS
                    //     rules keep applying without selector churn.
                    //   • `card-content--rich` (defined in `@shared/blocks/text/
                    //     view.scss`) is the SHARED rich-text content stylesheet
                    //     consumed by `<TiptapRender>` for saved cards and by
                    //     `<BlockDetailEditShell>` for the modal edit surface.
                    //     Reusing it here means lists, task lists, code chips,
                    //     blockquotes, links, and the empty-editor placeholder
                    //     all paint identically across capture → save → edit
                    //     transitions instead of being duplicated in this file's
                    //     SCSS. Surface-specific deltas (placeholder colour,
                    //     sibling-block gap, bold weight) are expressed as CSS
                    //     variable overrides scoped to `.omnibar-draft__input` in
                    //     `omnibar-host.scss`.
                    //
                    // No hidden mirror needed: contenteditable auto-grows
                    // naturally and is capped via CSS `max-height` + scroll on
                    // the parent.
                    _jsx(React.Suspense, { fallback: null, children: _jsx(RichTextEditor, { ref: editorRef, initialDoc: initialDocFromStorage, placeholder: placeholder, onChange: (nextDoc, nextText) => {
                                setDoc(nextDoc);
                                setText(nextText);
                            }, onFilePaste: uploadPastedFiles, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: handleKeyDown, className: "omnibar-draft__input card-content--rich" }) })) : (_jsxs(_Fragment, { children: [_jsx(ScrollView, { ref: textareaScrollRef, axis: "y", autoHide: "leave", className: "omnibar-draft__textarea-scroll", endInset: 8, children: _jsx("textarea", { ref: textareaRef, className: "omnibar-draft__input", placeholder: placeholder, rows: 1, value: text, 
                                    // `wrap="off"` switches the textarea into `white-space: pre`
                                    // mode, which lets CSS take over wrap behaviour entirely —
                                    // collapsed states clip with ellipsis, active states opt back
                                    // into wrapping via `white-space: pre-wrap`. With `wrap="soft"`
                                    // (default), browsers honour the attribute over CSS and force
                                    // wrapping no matter what. The `wrap` attribute does NOT
                                    // affect the submitted form value (same as `soft`); changing
                                    // it at runtime is also unreliable across browsers, so we set
                                    // it once at mount and let CSS drive the rest.
                                    wrap: "off", onChange: (e) => setText(e.target.value), onMouseDown: handleAskMouseDown, onFocus: handleAskFocus, onBlur: handleBlur, onKeyDown: handleKeyDown }) }), _jsx("textarea", { ref: mirrorRef, className: "omnibar-draft__input-mirror", value: text, readOnly: true, tabIndex: -1, "aria-hidden": "true" })] }))] }), mode === "add" && isExpanded ? _jsx(DraftToolbar, { editorRef: editorRef }) : null, _jsxs("div", { className: "omnibar-draft__row omnibar-draft__row--meta", children: [_jsx(MultiSelectPopover, { items: filteredPopoverItems, selectedIds: selectedCollectionIds, onChange: setSelectedCollectionIds, open: isPickerOpen, onOpenChange: handlePickerOpenChange, 
                        // The filter input replaces the historical "ADD TO COLLECTIONS"
                        // header — the popover surface is small enough that two
                        // stacked chrome rows (title + filter) crowds the list; the
                        // filter input is self-evidently a chooser entry point, so
                        // the title is redundant once we ship search.
                        emptyLabel: collectionFilter.trim().length > 0
                            ? `No collections match "${collectionFilter.trim()}"`
                            : "No collections yet", side: "top", align: "start", decoration: destinationDecoration, filter: destinationFilter, children: _jsx(DraftDestinationChip, { routeCollectionId: routeCollectionId, chipLabel: chipLabel, chipSwatch: chipSwatch, setSelectedCollectionIds: setSelectedCollectionIds }) }), mode === "add" ? (_jsx(DraftAttachments, { onFiles: handleAttachedFiles, isRecording: recorder.isRecording, onToggleRecording: handleToggleRecording })) : null] }), _jsx("button", { type: "button", className: "omnibar-draft__expand", 
                // Suppress the default mousedown focus shift so clicking the
                // toggle doesn't blur the textarea — we want the user's drafting
                // session to flow uninterrupted across the expand/collapse.
                onMouseDown: (e) => e.preventDefault(), onClick: handleToggleExpand, "aria-label": isExpanded ? "Collapse draft" : "Expand draft", tabIndex: -1, children: _jsx(FaIcon, { icon: isExpanded ? faDownLeftAndUpRightToCenter : faUpRightAndDownLeftFromCenter }) }), _jsxs("button", { type: "button", className: "omnibar-draft__mode-cluster", onMouseDown: (e) => e.preventDefault(), onClick: () => setMode((prev) => (prev === "add" ? "ask" : "add")), "aria-label": mode === "add" ? "Switch to Ask mode" : "Switch to Add mode", tabIndex: -1, children: [_jsx("span", { className: "omnibar-draft__mode-switch-slot", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faAdd }) }), _jsx("span", { className: "omnibar-draft__mode-switch-slot", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faQuestion }) }), _jsx("span", { className: "omnibar-draft__mode-switch-handle", "aria-hidden": "true" })] }), _jsxs(Button, { vibe: "primary", tone: "red", size: "mini", className: "omnibar-draft__send", noOutlineOnFocus: true, 
                // Keep focus on the editor / textarea while the press lands. On
                // mobile, letting the button pull focus blurs the input, dismisses
                // the keyboard, and reflows the viewport mid-tap — the synthesized
                // click then misses the button entirely and the user has to tap
                // again to actually send. With focus preserved, the click fires on
                // the button normally; `handleAddSend` then calls `editor.blur()`
                // so the keyboard dismisses after the send, completing the gesture
                // in a single tap.
                preserveFocus: true, "aria-label": mode === "ask"
                    ? "Ask — press Enter to submit"
                    : "Add — press Cmd or Ctrl + Enter to submit", disabled: canSend === false, onClick: handleSend, children: [_jsx("span", { className: "omnibar-draft__send-icon", "aria-hidden": "true", children: _jsx(FaIcon, { icon: faArrowUp }) }), _jsx("span", { className: "omnibar-draft__send-label", children: mode === "ask" ? "Ask" : "Add" }), _jsx(KeyboardShortcut, { keys: mode === "ask" ? ["↵"] : ["⌘", "↵"], size: "mini", tone: "dark", className: "omnibar-draft__send-shortcut", "aria-label": mode === "ask" ? "Press Enter" : "Press Cmd Enter" })] })] }));
});
